/**
 * <cos-reveal> — scroll-into-view orchestrator.
 *
 * On first intersection it:
 *   1. adds `.is-revealed` to itself  → CSS plays the staggered reveal of any
 *      descendant `[data-reveal-item]` (delay = `--reveal-i` * step).
 *   2. adds `.is-counting` to every descendant `.cos-count` → CSS plays the
 *      `@property` count-up (0 → `--count-to`).
 *
 * Progressive enhancement contract:
 *   - The hidden/initial state is gated on `.is-ready` (added here, on upgrade).
 *     If the module never runs, items stay visible — no FOUC trap.
 *   - `prefers-reduced-motion: reduce` → we never arm or observe; everything
 *     stays in its static, fully-visible state (literal `.cos-count__txt` shows).
 *   - Count-up is additionally feature-gated on `@property` support so older
 *     engines keep the literal number instead of a stuck "0".
 */

const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

// `@property` and `CSS.registerProperty` shipped together; presence of the JS
// API is a reliable proxy for the at-rule the count-up keyframes depend on.
const SUPPORTS_COUNT_ANIM = (() => {
  try {
    return typeof CSS !== 'undefined' && typeof CSS.registerProperty === 'function';
  } catch (_) {
    return false;
  }
})();

class CosReveal extends HTMLElement {
  #observer = null;

  connectedCallback() {
    // Respect reduced motion: leave content in its static, visible state.
    if (REDUCE_MOTION.matches) return;

    // Arm the hidden state only after upgrade — keeps content visible if JS dies.
    this.classList.add('is-ready');

    if (!('IntersectionObserver' in window)) {
      this.#reveal();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#reveal();
          observer.disconnect();
          this.#observer = null;
          break;
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #reveal() {
    this.classList.add('is-revealed');
    if (!SUPPORTS_COUNT_ANIM) return;
    const counters = this.querySelectorAll('.cos-count');
    for (const counter of counters) counter.classList.add('is-counting');
  }
}

if (!customElements.get('cos-reveal')) {
  customElements.define('cos-reveal', CosReveal);
}
